import React, { useCallback, useMemo, useState } from 'react';
import { DashboardHeader } from 'features/dashboard/dashboard-header/DashboardHeader';
import { SimpleDashboardContent } from 'features/dashboard/DashboardContent';
import { Column } from 'components/display/Table/types';
import { SimulationIdentifier, SimulationRow } from '../types/interfaces';
import { NsKey } from 'enums/NsKey';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import { SimulationTableRowKey } from '../types/enums';
import MoreBubble from 'components/ui/Bubble';
import GenericBadge from 'components/ui/Badge';
import BadgeTooltip from 'components/ui/BadgeTooltip';
import { IdLabel } from 'api/quote';
import { useNavigate } from 'react-router-dom';
import { Status } from 'types/optim/status';
import { useDialog } from 'hooks/useDialog';
import { Sizes } from 'components/forms/NumberSelector';
import SelectBrokerBranches from 'features/dashboard/dashboard-menu/SelectBrokerBranches';
import {
  BranchCode,
  StepIdentifier,
  GetGenericListCommandDto,
  GetRiskListResponseDto
} from 'api/risk';
import { risksAPI } from 'services/swagger-api/riskApi';
import {
  buildRoute,
  getStepRouteByStepIdentifierString
} from 'features/subscription-process/utils/getRouteByStepIdentifier';
import ServerTable from 'shared/contexts/table-context/components/ServerTable';
import {
  FacetQueryCommand,
  INCLUDE_TESTS_BOOLEAN_FACET,
  mapFacetFiltersToFiltersDto
} from 'shared/contexts/table-context/facetFilters';
import { buildExportCommand, exportListCsv } from 'shared/contexts/table-context/exportCsv';
import { useSharedViewDetection } from 'shared/contexts/table-context/useSharedViewDetection';
import { useRoleCan } from 'core/hooks/useRoleCan';
import { Capability } from 'core/roles';
import { SimulationFetchParams } from 'slices/generic-table/hooks/useSimulationTable';
import { mapRiskSummariesToSimulations } from '../mappers/mapRiskSummaryToSimulation';
import { TableResponse } from 'slices/generic-table/genericTableSlice';
import { Calculator, Trash2 } from 'lucide-react';
import { GenericViewHeader } from 'back-office/shared/components/GenericViewHeader';

interface SimulationViewProps {
  hideCreate?: boolean;
  /** Liste admin BO (toutes plateformes) : routes /admin/liste + colonne Courtier. */
  admin?: boolean;
}

// Colonnes masquées par défaut côté courtier (réactivables via « Colonnes »)
const COURTIER_HIDDEN_COLUMNS = ['quoteContract'];

const SimulationView: React.FC<SimulationViewProps> = ({ hideCreate = false, admin = false }) => {
  // Vue partagée : colonne Courtier si brokerName rempli par le back (détecteur données).
  const { isSharedView, detectFromItems } = useSharedViewDetection();
  // Droits pilotés par le rôle actif (manager central) : simu = Courtier uniquement
  // (admin & Plateforme en lecture seule) pour le clic ET la colonne d'actions.
  const canOpenProcess = useRoleCan(Capability.SimulationOpen);
  const canRowActions = useRoleCan(Capability.SimulationActions);
  const canSeeNotifBadges = useRoleCan(Capability.ListNotificationBadges);
  const { t } = useDynamicTranslation([NsKey.Common, NsKey.Risk]);
  const navigate = useNavigate();
  const { openBlankDialog } = useDialog();
  const [isNavigating, setIsNavigating] = useState(false);

  const onNewSimulation = useCallback(() => {
    openBlankDialog(
      {
        size: Sizes['5xl']
      },
      <SelectBrokerBranches />
    );
  }, [openBlankDialog]);

  const goToBorrowerSimulation = useCallback(
    async (borrowerProjectId: string, borrowerId: string) => {
      // Rôle sans droit d'ouverture du process : aucune étape ne doit s'ouvrir.
      if (!canOpenProcess) return;
      if (isNavigating) return;
      setIsNavigating(true);

      try {
        const response = await risksAPI.getSubscriptionProcessBorrower({
          projetEmpruntId: borrowerProjectId
        });

        const highestAccessibleStep =
          response?.subscriptionState?.highestAccessibleStep;
        if (!highestAccessibleStep) return;

        const routeConfig = getStepRouteByStepIdentifierString(
          BranchCode.Borrower,
          response.subscriptionState.highestAccessibleStep
        );
        if (!routeConfig) return;

        const isContractualization =
          highestAccessibleStep === StepIdentifier.Contractualization ||
          highestAccessibleStep === StepIdentifier.SignedContract;

        const quoteId = isContractualization
          ? response.subscriptionState.steps.find(
              (step) => step.identifier === StepIdentifier.Contractualization
            )?.subSteps[0].data?.quoteId || ''
          : undefined;

        const route = buildRoute(routeConfig.route, {
          borrowerProjectId,
          ...(quoteId && { quoteId }),
          ...(borrowerId && { borrowerId: borrowerId })
        });
        navigate(route);
      } catch (error) {
        console.error('Failed to fetch subscription process:', error);
      } finally {
        setIsNavigating(false);
      }
    },
    [navigate, isNavigating, canOpenProcess]
  );

  const goToSimulation = useCallback(
    async (
      branchCode: BranchCode,
      riskId: string,
      personId?: string,
      borrowerProjectId?: string
    ) => {
      // Rôle sans droit d'ouverture du process : aucune étape ne doit s'ouvrir.
      if (!canOpenProcess) return;
      if (branchCode === BranchCode.Borrower) {
        // Projet d'emprunt explicite (champ racine de la liste) ; repli legacy : l'id.
        return goToBorrowerSimulation(borrowerProjectId ?? riskId, personId || '');
      }

      if (isNavigating) return;
      setIsNavigating(true);

      try {
        const response = await risksAPI.getSubscriptionProcess({ riskId });

        const highestAccessibleStep =
          response?.subscriptionState?.highestAccessibleStep;
        if (!highestAccessibleStep) return;

        const routeConfig = getStepRouteByStepIdentifierString(
          branchCode,
          response.subscriptionState.highestAccessibleStep
        );
        if (!routeConfig) return;

        const isContractualization =
          highestAccessibleStep === StepIdentifier.Contractualization ||
          highestAccessibleStep === StepIdentifier.SignedContract;

        const quoteId = isContractualization
          ? response.subscriptionState.steps.find(
              (step) => step.identifier === StepIdentifier.Contractualization
            )?.subSteps[0].data?.quoteId || ''
          : undefined;

        const route = buildRoute(routeConfig.route, {
          riskId,
          ...(quoteId && { quoteId })
        });
        navigate(route);
      } catch (error) {
        console.error('Failed to fetch subscription process:', error);
      } finally {
        setIsNavigating(false);
      }
    },
    [navigate, isNavigating, goToBorrowerSimulation]
  );

  const columns: Column<SimulationRow>[] = useMemo(
    () => [
      {
        key: SimulationTableRowKey.DATE,
        label: t('COMMON.DATE'),
        sortable: true,
        sortKey: 'date',
        render: (value) => {
          const date = new Date(value as string);
          const day = date.getDate();
          const month = date.toLocaleDateString('fr-FR', { month: 'short' });
          const year = date.getFullYear();
          const time = date.toLocaleTimeString('fr-FR', {
            hour: '2-digit',
            minute: '2-digit'
          });

          return (
            <div>
              <div className="text-sm font-medium">
                {day} {month} {year}
              </div>
              <div className="text-xs text-gray-500">{time}</div>
            </div>
          );
        }
      },
      {
        key: SimulationTableRowKey.FOLDER_IDENTIFIER,
        label: t('RISK.LABELS.SIMULATION_NUMBER'),
        sortable: true,
        sortKey: 'identifier',
        render: (value, row) => {
          const simulationIdentifier = value as SimulationIdentifier;
          // Pastille de notifications : Courtier uniquement (matrice centrale)
          const unreadCount = canSeeNotifBadges ? row[SimulationTableRowKey.UNREAD_NOTIFICATION_COUNT] || 0 : 0;
          return (
            <div
              onClick={
                canOpenProcess
                  ? () =>
                      goToSimulation(
                        simulationIdentifier.branchCode,
                        simulationIdentifier.id,
                        row.riskId,
                        row.borrowerProjectId
                      )
                  : undefined
              }
              className={
                canOpenProcess
                  ? 'cursor-pointer inline-flex items-center gap-2'
                  : 'inline-flex items-center gap-2'
              }
            >
              <span
                className={
                  canOpenProcess
                    ? 'font-bold text-primary hover:underline'
                    : 'font-bold text-gray-700'
                }
              >
                {simulationIdentifier && simulationIdentifier.label}
              </span>
              {unreadCount > 0 && (
                <BadgeTooltip content={`${unreadCount} notification${unreadCount > 1 ? 's' : ''} non lue${unreadCount > 1 ? 's' : ''}`}>
                  <span className="min-w-[18px] h-[18px] px-1 flex items-center justify-center bg-secondary text-white text-[10px] font-semibold rounded-full">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                </BadgeTooltip>
              )}
            </div>
          );
        }
      },
      {
        key: SimulationTableRowKey.INSUREDS,
        label: t('COMMON.LABELS.INSURED_PERSONS'),
        sortKey: 'insured'
      },
      {
        // Produits multi-valués sur une simulation : pas de tri (consigne back)
        key: SimulationTableRowKey.PRODUCTS,
        label: t('COMMON.LABELS.PRODUCTS')
      },
      {
        key: SimulationTableRowKey.QUOTE_CONTRACT,
        label: t('RISK.LABELS.CONTRACT_QUOTE_NUMBER'),
        render: (value, row) => {
          const bubbleItems = (value as IdLabel[]).map((item) => ({
            label: item.label,
            // Liens vers la contractualisation neutralisés si le rôle n'a pas le droit
            // d'ouvrir le process (matrice centrale).
            action: canOpenProcess
              ? () =>
                  navigate(`/simulation/contractualisation/${row.riskId}/${item.id}`)
              : undefined
          }));
          return <MoreBubble items={bubbleItems} />;
        }
      },
      {
        // Provenance : badge coloré selon le code (Espace pro / Espace membre)
        key: SimulationTableRowKey.SOURCE,
        label: 'Provenance',
        render: (value, row) => {
          const label = (value as string) || '—';
          if (label === '—') return <span className="text-gray-400">—</span>;
          const isMembre = row.sourceCode === 'ESPACE_MEMBRE';
          return (
            <span
              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                isMembre
                  ? 'bg-teal-100 text-teal-800'
                  : 'bg-indigo-100 text-indigo-800'
              }`}
            >
              {label}
            </span>
          );
        }
      },
      // Colonne Courtier : admin OU vue partagée (brokerName rempli). Tri courtier
      // garanti seulement en admin -> pas de sortKey hors admin.
      ...(admin || isSharedView
        ? ([
            {
              key: 'brokerName',
              label: 'Courtier',
              ...(admin ? { sortKey: 'broker' } : {})
            }
          ] as Column<SimulationRow>[])
        : []),
      // Colonne Plateforme : admin uniquement.
      ...(admin
        ? ([
            {
              key: 'plateformeName',
              label: 'Plateforme',
              sortKey: 'plateforme'
            }
          ] as Column<SimulationRow>[])
        : []),
      {
        key: SimulationTableRowKey.STATUS,
        label: t('COMMON.STATUS'),
        sortable: true,
        sortKey: 'status',
        render: (value) => {
          const status = value as Status;
          return (
            <GenericBadge
              label={status.label}
              color={status.color}
              badgeType="sm-bold"
            />
          );
        }
      }
    ],
    [t, navigate, goToSimulation, admin, isSharedView, canOpenProcess, canSeeNotifBadges]
  );

  const actions = useMemo(
    () => ({
      ...(hideCreate ? {} : {
        create: {
          label: t('FEATURES.ACTIONS.NEW_SIMULATION'),
          onClick: onNewSimulation
        }
      }),
      bulk: [
        {
          label: t('COMMON.DELETE'),
          icon: <Trash2 size={16} />,
          variant: 'danger' as const,
          onClick: (rows: SimulationRow[]) => {
            console.log('Delete simulations:', rows.length);
          }
        }
      ]
    }),
    [t, onNewSimulation, hideCreate]
  );

  const fetchSimulations = useCallback(
    async (
      params: SimulationFetchParams
    ): Promise<TableResponse<SimulationRow>> => {
      try {
        const command: GetGenericListCommandDto = {
          pageNumber: params.pageNumber,
          pageSize: params.pageSize,
          searchString: params.searchString || undefined,
          // Tri serveur par colonne (omis -> tri par défaut du back)
          sortBy: (params as any).sortBy,
          sortDirection: (params as any).sortDirection,
          // Filtres du panneau à facettes (indexés par facet.key) -> FiltersDto
          filters: mapFacetFiltersToFiltersDto((params as any).filters)
        };

        const result: GetRiskListResponseDto = admin
          ? await risksAPI.getAdminSimulations({ getGenericListCommandDto: command })
          : await risksAPI.getSimulations({ getGenericListCommandDto: command });

        const items = result.items || [];
        let mappedSimulations = mapRiskSummariesToSimulations(items);

        if (admin) {
          // La liste admin porte en plus les dimensions courtier / plateforme
          mappedSimulations = mappedSimulations.map((row, index) => ({
            ...row,
            brokerName: (items[index] as any)?.brokerName,
            plateformeName: (items[index] as any)?.plateformeName
          }));
        } else {
          // Vue partagée : brokerName rempli par le back sur les routes courtier.
          mappedSimulations = mappedSimulations.map((row, index) => ({
            ...row,
            brokerName: (items[index] as any)?.brokerName
          }));
          detectFromItems(items as Array<{ brokerName?: string | null }>);
        }

        let filteredSimulations = mappedSimulations;

        if (params.branchFilter) {
          filteredSimulations = filteredSimulations.filter((sim) =>
            sim.branch?.toLowerCase().includes(params.branchFilter!.toLowerCase())
          );
        }

        if (params.dateRange) {
          filteredSimulations = filteredSimulations.filter((sim) => {
            const simDate = new Date(sim.date);
            return (
              simDate >= params.dateRange!.start &&
              simDate <= params.dateRange!.end
            );
          });
        }

        return {
          items: filteredSimulations,
          totalItems: result.totalCount || filteredSimulations.length,
          totalPages: Math.ceil(
            (result.totalCount || filteredSimulations.length) / params.pageSize
          ),
          currentPage: params.pageNumber
        };
      } catch (error) {
        console.error('Erreur lors de la récupération des simulations:', error);
        throw error;
      }
    },
    [admin, detectFromItems]
  );

  const fetchSimulationFacets = useCallback(
    (command: FacetQueryCommand) =>
      admin
        ? risksAPI.getAdminSimulationListFacets({ getGenericListCommandDto: command })
        : risksAPI.getSimulationListFacets({ getGenericListCommandDto: command }),
    [admin]
  );

  return (
    <SimpleDashboardContent>
      {/* Barre de recherche globale : espace courtier uniquement, pas en admin BO */}
      {!admin && <DashboardHeader />}
      {/* Identité de page admin (le titre du tableau est porté par ce header) */}
      {admin && <GenericViewHeader title="Simulations" icon={Calculator} section="Activité" />}
      <ServerTable<SimulationRow>
        apiConfig={{
          fetchData: fetchSimulations
        }}
        columns={columns}
        title={admin ? undefined : {
          iconKey: 'calculator',
          label: t('FEATURES.TITLE.MY_SIMULATIONS')
        }}
        actions={canRowActions ? actions : undefined}
        selectable
        serverSorting
        centerColumns
        columnCustomization={{
          storageKey: admin ? 'bo-simulations' : 'courtier-simulations',
          defaultHidden: admin ? undefined : COURTIER_HIDDEN_COLUMNS
        }}
        facets={{ fetchFacets: fetchSimulationFacets, booleanFacets: INCLUDE_TESTS_BOOLEAN_FACET }}
        // Export CSV (admin) : rejoue la commande courante sur la route .../csv
        onExport={
          admin
            ? (p) =>
                exportListCsv(
                  '/api/backoffice/risques/admin/liste/csv',
                  buildExportCommand(p, mapFacetFiltersToFiltersDto(p.filters))
                )
            : undefined
        }
      />
    </SimpleDashboardContent>
  );
};

export default SimulationView;
